/**
 * Aba "Solicitações" — merge de assistências + requisições, vistas
 * Tabela/Kanban/Linha do tempo/Calendário, detalhe em SlideOverPanel e
 * geração de lote MDF.
 *
 * FILTROS (padrão Excel): busca ao vivo + um dropdown multi-seleção por
 * dimensão (Tipo, Situação, Cliente, Solicitante, Período), cada um com
 * busca interna, "(Selecionar tudo)" e contagens calculadas sob os DEMAIS
 * filtros (como o Excel). Set vazio = dimensão sem filtro. Linha de chips
 * mostra o que está ativo, com remoção individual e "Limpar filtros".
 * Com exatamente 1 tipo selecionado, o kanban usa as colunas configuradas
 * daquele tipo; senão a configuração geral.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FiCalendar, FiClock, FiColumns, FiList, FiSave, FiSearch, FiSliders, FiX,
} from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import { useTabelaPreferenciaUI } from "../../../hooks/useTabelaPreferencia";
import {
  SITUACOES_SOLICITACAO,
  atenderSolicitacao,
  cancelarSolicitacao,
  iniciarSolicitacao,
  voltarParaAutorizada,
  listarAssistencias,
  listarRequisicoes,
  moverEtapaSolicitacao,
  obterConfigColunas,
  podeAcaoTipo,
  reabrirSolicitacao,
  recusarSolicitacao,
  tiposComAcao,
} from "../../../services/solicitacoes";
import AutorizarClassificarModal from "./AutorizarClassificarModal";
import Calendario from "./Calendario";
import ColunasKanbanPanel from "./ColunasKanbanPanel";
import ComprovantesLoteModal from "./ComprovantesLoteModal";
import DetalheSlideOver from "./DetalheSlideOver";
import EncerrarSolicitacaoModal from "./EncerrarSolicitacaoModal";
import FiltroExcel from "./FiltroExcel";
import FiltroPeriodo from "./FiltroPeriodo";
import GerarLoteModal from "./GerarLoteModal";
import JuntarModal from "./JuntarModal";
import Kanban, {
  situacaoAlvoReabertura,
  transicaoParaColuna,
} from "./Kanban";
import LinhaTempo from "./LinhaTempo";
import RetiradaModal from "./RetiradaModal";
import ReabrirModal from "./ReabrirModal";
import Tabela from "./Tabela";
import {
  TIPO_LABELS, chaveSolicitacao, elegivelParaLote, motivoParaNaoJuntar, regraDeSelecao,
  unwrapLista,
} from "./helpers";
import {
  ControlesBar,
  ErrorMsg,
  FexBar,
  FexChip,
  FexChipsRow,
  GhostBtn,
  PrimaryBtn,
  StatChip,
  StatsRow,
  StickyActions,
  StickyBar,
  StickyInfo,
  ToggleBtn,
  ViewToggle,
} from "./styles";
import { Carregando, Vazio } from "../_shared/Estados";

const TODOS_TIPOS_SIGLAS = ["ASM", "AST", "ASP", "RQA", "RQI"];

const VISTAS = [
  { value: "tabela", label: "Tabela", Icon: FiList },
  { value: "kanban", label: "Kanban", Icon: FiColumns },
  { value: "linha", label: "Linha do tempo", Icon: FiClock },
  { value: "calendario", label: "Calendário", Icon: FiCalendar },
];

// Valor especial dos dropdowns para "(Vazias)" — igual ao Excel.
const VAZIO = "__vazio__";

// Visualização padrão do usuário (vista + filtros), persistida no backend em
// PreferenciaDashboard.preferencias_tabelas.ui — logo vale em QUALQUER
// dispositivo (celular, tablet, desktop). A busca livre NÃO entra: é
// transitória, salvá-la como padrão só atrapalharia.
const CHAVE_VIS = "gestao_solicitacoes";
const VISTA_PADRAO = "tabela";

const setParaLista = (s) => (s ? [...s].sort() : null);
const listaParaSet = (a) => (Array.isArray(a) ? new Set(a) : null);

/** Assinatura canônica p/ comparar visualizações (ordem de chave estável). */
function chaveVis(v) {
  return JSON.stringify([
    v?.vista ?? VISTA_PADRAO,
    v?.tipos ?? null,
    v?.situacoes ?? null,
    v?.clientes ?? null,
    v?.solicitantes ?? null,
    v?.periodo?.de ?? null,
    v?.periodo?.ate ?? null,
  ]);
}

// Fallback caso a config de colunas não carregue (mesmo default do backend).
const COLUNAS_PADRAO = [
  { id: "aguardando", titulo: "Aguardando autorização", cor: "#F59E0B", situacoes: ["ABERTA"] },
  { id: "autorizadas", titulo: "Autorizadas", cor: "#38BDF8", situacoes: ["AUTORIZADA"] },
  { id: "em_andamento", titulo: "Em andamento", cor: "#A78BFA", situacoes: ["EM_ANDAMENTO"] },
  { id: "atendidas", titulo: "Atendidas", cor: "#34D399", situacoes: ["ATENDIDA"] },
  { id: "encerradas", titulo: "Recusadas/Canceladas", cor: "#F87171", situacoes: ["RECUSADA", "CANCELADA"] },
];

function isoLocal(data) {
  const d = new Date(data);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

export default function SolicitacoesTab() {
  const { request, hasPermission } = useAuth();
  // Preferência pessoal (mesma linha do usuário no backend → cross-device).
  const { ui, getUI, setUI, flush: flushPref, estado: estadoPref } =
    useTabelaPreferenciaUI();
  // Vira true no primeiro toque do usuário em vista/filtro: impede que a
  // hidratação (assíncrona) atropele o que ele já mexeu enquanto carregava.
  const mexeuRef = useRef(false);
  const marcarMexido = useCallback(() => {
    mexeuRef.current = true;
  }, []);

  const podeGerarLote = hasPermission("solicitacao:gerar_lote");
  // A caixa de seleção serve ao lote MDF e à junção (23/09/2026).
  const podeJuntar = hasPermission("solicitacao:juntar");
  const mostrarSelecao = podeGerarLote || podeJuntar;
  const selecao = useMemo(
    () => regraDeSelecao({ podeGerarLote, podeJuntar }),
    [podeGerarLote, podeJuntar],
  );
  // Acesso por TIPO (RBAC granular): tipos que o usuário pode VER e GERIR.
  const tiposVisiveis = useMemo(
    () => tiposComAcao(hasPermission, "view", TODOS_TIPOS_SIGLAS),
    [hasPermission],
  );
  const tiposGeridos = useMemo(
    () =>
      TODOS_TIPOS_SIGLAS.filter(
        (t) =>
          podeAcaoTipo(hasPermission, "autorizar", t) ||
          podeAcaoTipo(hasPermission, "atender", t),
      ),
    [hasPermission],
  );
  // Gate grosseiro do drag; o backend valida a transição/permissão exata.
  const podeMover = tiposGeridos.length > 0 || podeGerarLote;

  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [vista, setVista] = useState("tabela");
  const [selecionadas, setSelecionadas] = useState(() => new Set());
  const [detalhe, setDetalhe] = useState(null);
  const [loteAberto, setLoteAberto] = useState(false);
  const [juntarAberto, setJuntarAberto] = useState(false);
  const [encerrar, setEncerrar] = useState(null);
  // Autorizar passa pelo modal "Autorizar e classificar" (Transition Form).
  // `coluna` vem do kanban: depois de autorizar, o card vai para a coluna.
  const [autorizar, setAutorizar] = useState(null);
  // Atender exige a retirada assinada completa (22/09/2026): soltar na
  // coluna de atendidas sem retirada abre o registro da retirada primeiro.
  const [retirar, setRetirar] = useState(null);
  // Reabrir pede motivo (PM-02): card finalizado solto numa coluna ativa.
  const [reabrir, setReabrir] = useState(null);
  // ?abrir=NÚMERO (link vindo do Painel de Assistências) abre o detalhe.
  const [searchParams, setSearchParams] = useSearchParams();
  const abrirNumero = searchParams.get("abrir");
  const [colunasKanban, setColunasKanban] = useState(COLUNAS_PADRAO);
  const [configColunasAberto, setConfigColunasAberto] = useState(false);
  // Lote recém-gerado: abre os comprovantes das solicitações do lote.
  const [comprovantesDe, setComprovantesDe] = useState(null);

  // ---- Filtros (padrão Excel): Set vazio = dimensão sem filtro ----
  const [busca, setBusca] = useState("");
  const [fTipos, setFTipos] = useState(null);
  const [fSituacoes, setFSituacoes] = useState(null);
  const [fClientes, setFClientes] = useState(null);
  const [fSolicitantes, setFSolicitantes] = useState(null);
  const [fPeriodo, setFPeriodo] = useState({ de: null, ate: null });

  const carregar = useCallback(
    async (silencioso = false) => {
      if (!silencioso) setLoading(true);
      setErro(null);
      try {
        const [assist, reqs] = await Promise.all([
          listarAssistencias(request),
          listarRequisicoes(request),
        ]);
        const lista = [
          ...unwrapLista(assist).map((s) => ({ ...s, familia: "assistencia" })),
          ...unwrapLista(reqs).map((s) => ({ ...s, familia: "requisicao" })),
        ].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        setItens(lista);
      } catch (e) {
        setErro(e?.message || "Erro ao carregar as solicitações.");
      } finally {
        if (!silencioso) setLoading(false);
      }
    },
    [request],
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  const recarregarSilencioso = useCallback(() => carregar(true), [carregar]);

  // Abre o detalhe pedido por ?abrir= assim que a lista chega; limpa o
  // parâmetro para o refresh não reabrir o painel.
  useEffect(() => {
    if (!abrirNumero || loading) return;
    const alvo = itens.find((s) => s.numero === abrirNumero);
    if (alvo) setDetalhe({ familia: alvo.familia, id: alvo.id, numero: alvo.numero });
    setSearchParams((prev) => {
      const prox = new URLSearchParams(prev);
      prox.delete("abrir");
      return prox;
    }, { replace: true });
  }, [abrirNumero, loading, itens, setSearchParams]);

  // Com exatamente 1 tipo filtrado, o kanban usa a config daquele tipo.
  const tipoAba = fTipos && fTipos.size === 1 ? [...fTipos][0] : "TODOS";

  // Colunas configuráveis do kanban POR TIPO. Fallback = COLUNAS_PADRAO.
  const carregarColunas = useCallback(() => {
    obterConfigColunas(request, tipoAba)
      .then((d) => setColunasKanban(d?.colunas?.length ? d.colunas : COLUNAS_PADRAO))
      .catch(() => {});
  }, [request, tipoAba]);

  useEffect(() => {
    carregarColunas();
  }, [carregarColunas]);

  // Passa em TODOS os filtros, exceto a dimensão `ignorar` (contagens
  // por dimensão no padrão Excel: cada dropdown conta sob os OUTROS filtros).
  const passa = useCallback(
    (s, ignorar = "") => {
      const q = busca.trim().toLowerCase();
      if (
        q &&
        ![s.numero, s.num_pedido, s.cliente_nome, s.criado_por_nome].some((v) =>
          String(v || "").toLowerCase().includes(q),
        )
      ) {
        return false;
      }
      // null = dimensão sem filtro. Set (mesmo VAZIO) = seleção explícita —
      // Set vazio não deixa nada passar (padrão Excel: desmarcar tudo).
      if (ignorar !== "tipos" && fTipos && !fTipos.has(s.tipo)) return false;
      if (ignorar !== "situacoes" && fSituacoes && !fSituacoes.has(s.situacao)) {
        return false;
      }
      if (ignorar !== "clientes" && fClientes) {
        if (!fClientes.has(s.cliente_nome || VAZIO)) return false;
      }
      if (ignorar !== "solicitantes" && fSolicitantes) {
        if (!fSolicitantes.has(s.criado_por_nome || VAZIO)) return false;
      }
      if (ignorar !== "periodo" && (fPeriodo.de || fPeriodo.ate)) {
        const d = s.created_at ? isoLocal(s.created_at) : "";
        if (!d) return false;
        if (fPeriodo.de && d < fPeriodo.de) return false;
        if (fPeriodo.ate && d > fPeriodo.ate) return false;
      }
      return true;
    },
    [busca, fTipos, fSituacoes, fClientes, fSolicitantes, fPeriodo],
  );

  const filtradas = useMemo(() => itens.filter((s) => passa(s)), [itens, passa]);
  // Linha do tempo: o período filtra a data do EVENTO, não a de criação.
  const semFiltroDePeriodo = useMemo(
    () => itens.filter((s) => passa(s, "periodo")),
    [itens, passa],
  );

  // ---- Opções dos dropdowns (com contagens sob os demais filtros) ----
  const opcoesTipo = useMemo(() => {
    const base = itens.filter((s) => passa(s, "tipos"));
    return tiposVisiveis.map((t) => ({
      value: t,
      label: `${t} · ${TIPO_LABELS[t] || t}`,
      count: base.filter((s) => s.tipo === t).length,
    }));
  }, [itens, passa, tiposVisiveis]);

  const opcoesSituacao = useMemo(() => {
    const base = itens.filter((s) => passa(s, "situacoes"));
    // Juntada não vem na lista da Gestão (saiu do quadro): sem opção de filtro.
    return SITUACOES_SOLICITACAO.filter((s) => s.value !== "JUNTADA").map((s) => ({
      value: s.value,
      label: s.label,
      count: base.filter((i) => i.situacao === s.value).length,
    }));
  }, [itens, passa]);

  const opcoesDeCampo = useCallback(
    (campo, dimensao, rotuloVazio, selecionadasAtuais) => {
      const base = itens.filter((s) => passa(s, dimensao));
      const contagem = new Map();
      base.forEach((s) => {
        const v = s[campo] || VAZIO;
        contagem.set(v, (contagem.get(v) || 0) + 1);
      });
      // Valores JÁ selecionados ficam na lista mesmo com 0 ocorrências
      // (senão o usuário não conseguiria desmarcá-los — padrão Excel).
      (selecionadasAtuais || []).forEach((v) => {
        if (!contagem.has(v)) contagem.set(v, 0);
      });
      const valores = [...contagem.keys()].sort((a, b) => {
        if (a === VAZIO) return 1;
        if (b === VAZIO) return -1;
        return String(a).localeCompare(String(b), "pt-BR");
      });
      return valores.map((v) => ({
        value: v,
        label: v === VAZIO ? rotuloVazio : v,
        count: contagem.get(v) || 0,
      }));
    },
    [itens, passa],
  );

  const opcoesCliente = useMemo(
    () => opcoesDeCampo("cliente_nome", "clientes", "(Sem cliente)", fClientes),
    [opcoesDeCampo, fClientes],
  );
  const opcoesSolicitante = useMemo(
    () =>
      opcoesDeCampo(
        "criado_por_nome", "solicitantes", "(Sem solicitante)", fSolicitantes,
      ),
    [opcoesDeCampo, fSolicitantes],
  );

  // ---- Visualização padrão do usuário (cross-device) ----
  // Snapshot do que está na tela agora (Sets viram listas ordenadas: JSON
  // não serializa Set, e a ordem estável deixa a comparação confiável).
  const visAtual = useMemo(
    () => ({
      v: 1,
      vista,
      tipos: setParaLista(fTipos),
      situacoes: setParaLista(fSituacoes),
      clientes: setParaLista(fClientes),
      solicitantes: setParaLista(fSolicitantes),
      periodo: { de: fPeriodo.de || null, ate: fPeriodo.ate || null },
    }),
    [vista, fTipos, fSituacoes, fClientes, fSolicitantes, fPeriodo],
  );

  const visSalva = ui?.[CHAVE_VIS] || null;
  // Sem padrão salvo, a referência é a visualização de fábrica (tabela, sem
  // filtro) — assim o botão só aparece quando o usuário realmente mudou algo.
  const visMudou = chaveVis(visAtual) !== chaveVis(visSalva);

  // Aplica o padrão salvo UMA vez, quando a preferência termina de carregar.
  // Lê por getUI() (cache do provider) e NÃO pelo `ui` de estado: este chega
  // um render depois de `estadoPref` virar 'pronto' e o padrão nunca seria
  // aplicado. `visHidratada` também segura o botão até essa 1ª passada.
  const hidratouVis = useRef(false);
  const [visHidratada, setVisHidratada] = useState(false);
  useEffect(() => {
    if (hidratouVis.current || estadoPref === "loading") return;
    hidratouVis.current = true;
    setVisHidratada(true);
    // Backend fora ('erro_inicial') ou usuário já mexeu: não sobrescreve.
    if (estadoPref !== "pronto" || mexeuRef.current) return;
    const v = getUI()?.[CHAVE_VIS];
    if (!v) return;
    if (VISTAS.some((x) => x.value === v.vista)) setVista(v.vista);
    setFTipos(listaParaSet(v.tipos));
    setFSituacoes(listaParaSet(v.situacoes));
    setFClientes(listaParaSet(v.clientes));
    setFSolicitantes(listaParaSet(v.solicitantes));
    setFPeriodo({ de: v.periodo?.de || null, ate: v.periodo?.ate || null });
  }, [estadoPref, getUI]);

  const salvarVisualizacao = async () => {
    setUI({ [CHAVE_VIS]: visAtual });
    // flush explícito (sem ele o PUT só sairia no debounce de 2s) e com
    // propagarErro para o AsyncButton avisar em vez de mentir "salvo".
    await flushPref({ propagarErro: true });
  };

  const temFiltros =
    Boolean(fTipos) ||
    Boolean(fSituacoes) ||
    Boolean(fClientes) ||
    Boolean(fSolicitantes) ||
    Boolean(fPeriodo.de || fPeriodo.ate);

  const limparFiltros = () => {
    marcarMexido();
    setFTipos(null);
    setFSituacoes(null);
    setFClientes(null);
    setFSolicitantes(null);
    setFPeriodo({ de: null, ate: null });
  };

  // `rotuloVazio` espelha o texto do dropdown ("(Sem cliente)" etc.) para o
  // chip e o painel falarem a mesma língua. Set vazio = "nada marcado".
  const resumoChip = (set, mapa, rotuloVazio = "(Vazias)") => {
    const nomes = [...(set || [])].map((v) =>
      v === VAZIO ? rotuloVazio : mapa ? mapa[v] || v : v,
    );
    if (nomes.length === 0) return "(nada marcado)";
    return nomes.length > 2
      ? `${nomes.slice(0, 2).join(", ")} +${nomes.length - 2}`
      : nomes.join(", ");
  };

  const SITUACAO_LABEL_CURTO = useMemo(
    () =>
      Object.fromEntries(SITUACOES_SOLICITACAO.map((s) => [s.value, s.label])),
    [],
  );

  // KPIs escopados nos TIPOS filtrados (sem filtro = todos).
  const itensDoTipo = useMemo(
    () => (fTipos ? itens.filter((s) => fTipos.has(s.tipo)) : itens),
    [itens, fTipos],
  );

  const kpis = useMemo(() => {
    const conta = (sit) => itensDoTipo.filter((s) => s.situacao === sit).length;
    const agora = new Date();
    const atendidasMes = itensDoTipo.filter((s) => {
      if (s.situacao !== "ATENDIDA") return false;
      const ref = new Date(s.atendida_em || s.created_at || 0);
      return (
        ref.getMonth() === agora.getMonth() && ref.getFullYear() === agora.getFullYear()
      );
    }).length;
    return {
      abertas: conta("ABERTA"),
      autorizadas: conta("AUTORIZADA"),
      emAndamento: conta("EM_ANDAMENTO"),
      atendidasMes,
    };
  }, [itensDoTipo]);

  // KPI clicável = filtro de situação única (clicar de novo desliga).
  const situacaoKpiAtiva = (sit) =>
    Boolean(fSituacoes) && fSituacoes.size === 1 && fSituacoes.has(sit);
  const toggleKpiSituacao = (sit) => {
    marcarMexido();
    setFSituacoes((prev) =>
      prev && prev.size === 1 && prev.has(sit) ? null : new Set([sit]),
    );
  };

  // "Atendidas no mês" conta SÓ o mês corrente — o clique aplica situação E
  // período do mês, senão o número do chip divergiria das linhas filtradas.
  const periodoMesCorrente = () => {
    const agora = new Date();
    return {
      de: isoLocal(new Date(agora.getFullYear(), agora.getMonth(), 1)),
      ate: isoLocal(agora),
    };
  };
  const kpiAtendidasMesAtivo = (() => {
    const p = periodoMesCorrente();
    return (
      situacaoKpiAtiva("ATENDIDA") &&
      fPeriodo.de === p.de &&
      fPeriodo.ate === p.ate
    );
  })();
  const toggleKpiAtendidasMes = () => {
    marcarMexido();
    if (kpiAtendidasMesAtivo) {
      setFSituacoes(null);
      setFPeriodo({ de: null, ate: null });
    } else {
      setFSituacoes(new Set(["ATENDIDA"]));
      setFPeriodo(periodoMesCorrente());
    }
  };

  const solSelecionadas = useMemo(
    () => itens.filter((s) => selecionadas.has(chaveSolicitacao(s))),
    [itens, selecionadas],
  );
  const totalPecasSelecionadas = solSelecionadas.reduce(
    (acc, s) => acc + (s.pecas_elegiveis_count || 0),
    0,
  );
  // Pedidos/clientes diferentes PODEM entrar no mesmo lote (decisão da
  // gestora 2026-07-07 — otimizar o corte junto). A contagem só informa.
  const pedidosSelecionados = useMemo(
    () => Array.from(new Set(solSelecionadas.map((s) => s.num_pedido).filter(Boolean))),
    [solSelecionadas],
  );

  const toggleSelecao = useCallback((s) => {
    setSelecionadas((prev) => {
      const prox = new Set(prev);
      const chave = chaveSolicitacao(s);
      if (prox.has(chave)) prox.delete(chave);
      else prox.add(chave);
      return prox;
    });
  }, []);

  const toggleTodas = useCallback(() => {
    const elegiveis = filtradas.filter(selecao.pode);
    setSelecionadas((prev) => {
      const todasMarcadas =
        elegiveis.length > 0 && elegiveis.every((s) => prev.has(chaveSolicitacao(s)));
      if (todasMarcadas) {
        const prox = new Set(prev);
        elegiveis.forEach((s) => prox.delete(chaveSolicitacao(s)));
        return prox;
      }
      const prox = new Set(prev);
      elegiveis.forEach((s) => prox.add(chaveSolicitacao(s)));
      return prox;
    });
  }, [filtradas, selecao]);

  const loteDaSelecao = solSelecionadas.length > 0 && solSelecionadas.every(elegivelParaLote);
  const motivoSemJuncao = motivoParaNaoJuntar(solSelecionadas);

  const abrirDetalhe = useCallback((s) => {
    setDetalhe({ familia: s.familia, id: s.id, numero: s.numero });
  }, []);

  const aoGerarLote = useCallback(
    (lote) => {
      setLoteAberto(false);
      setSelecionadas(new Set());
      recarregarSilencioso();
      // Oferece na hora os comprovantes das solicitações do lote gerado.
      if (lote?.id) {
        setComprovantesDe({ id: lote.id, numero: lote.numero });
      }
    },
    [recarregarSilencioso],
  );

  const aoJuntar = useCallback(
    (principal) => {
      setJuntarAberto(false);
      setSelecionadas(new Set());
      recarregarSilencioso();
      if (principal?.id) {
        setDetalhe({
          familia: solSelecionadas[0]?.familia, id: principal.id, numero: principal.numero,
        });
      }
    },
    [recarregarSilencioso, solSelecionadas],
  );

  // Move o card na hora (otimista) e sincroniza com o servidor depois; em
  // erro, o reload silencioso reverte (o wrapper `request` já mostra o toast).
  const moverComOtimista = useCallback(
    async (sol, patch, chamarApi) => {
      const chave = chaveSolicitacao(sol);
      setItens((prev) =>
        prev.map((i) =>
          chaveSolicitacao(i) === chave ? { ...i, ...patch } : i,
        ),
      );
      try {
        await chamarApi();
      } catch (e) {
        /* toast automático; o reload abaixo reconcilia/reverte */
      }
      recarregarSilencioso();
    },
    [recarregarSilencioso],
  );

  // Drop no kanban: dispara a transição de status (se a coluna ancora uma) E
  // move o card para a coluna (etapa_quadro). Recusar/cancelar via modal.
  const aoMoverKanban = useCallback(
    (sol, coluna) => {
      const tipo = transicaoParaColuna(sol.situacao, coluna.situacoes);
      if (tipo === "encerrar") {
        setEncerrar(sol);
        return;
      }
      // Reabrir card finalizado: volta ao status ativo ancorado pela coluna.
      if (tipo === "reabrir") {
        const alvo = situacaoAlvoReabertura(coluna.situacoes);
        if (!alvo) return;
        setReabrir({ sol, coluna, alvo });
        return;
      }
      // Autorizar coleta a classificação no modal; o card move depois.
      if (tipo === "autorizar") {
        setAutorizar({ sol, coluna });
        return;
      }
      if (tipo === "atender" && !sol.expedida_em) {
        setRetirar({ sol, coluna });
        return;
      }
      const novaSituacao = {
        iniciar: "EM_ANDAMENTO",
        voltar: "AUTORIZADA",
        atender: "ATENDIDA",
      }[tipo] || sol.situacao;
      moverComOtimista(
        sol,
        { situacao: novaSituacao, etapa_quadro: coluna.id },
        async () => {
          if (tipo === "iniciar") {
            await iniciarSolicitacao(request, sol.familia, sol.id);
          } else if (tipo === "voltar") {
            await voltarParaAutorizada(request, sol.familia, sol.id);
          } else if (tipo === "atender") {
            await atenderSolicitacao(request, sol.familia, sol.id);
          }
          await moverEtapaSolicitacao(request, sol.familia, sol.id, coluna.id);
        },
      );
    },
    [request, moverComOtimista],
  );

  // Depois de autorizar pelo modal: se veio do kanban, leva o card à coluna.
  const aposAutorizar = useCallback(async () => {
    const alvo = autorizar;
    setAutorizar(null);
    if (alvo?.coluna) {
      try {
        await moverEtapaSolicitacao(request, alvo.sol.familia, alvo.sol.id, alvo.coluna.id);
      } catch (e) {
        /* toast automático do wrapper; o reload reconcilia */
      }
    }
    recarregarSilencioso();
  }, [autorizar, request, recarregarSilencioso]);

  // Retirada registrada a partir do arraste: completa → atende (se a
  // configuração já não atendeu) e leva o card à coluna; parcial → fica.
  const aposRetirar = useCallback(async (resp) => {
    const alvo = retirar;
    setRetirar(null);
    const completa = Boolean(resp?.expedida_em || resp?.retirada?.completa);
    if (alvo && completa) {
      try {
        if (resp?.situacao !== "ATENDIDA") {
          await atenderSolicitacao(request, alvo.sol.familia, alvo.sol.id);
        }
        await moverEtapaSolicitacao(request, alvo.sol.familia, alvo.sol.id, alvo.coluna.id);
      } catch (e) {
        /* toast automático do wrapper; o reload reconcilia */
      }
    }
    recarregarSilencioso();
  }, [retirar, request, recarregarSilencioso]);

  const confirmarReabrir = useCallback(async (motivo) => {
    const alvo = reabrir;
    if (!alvo) return;
    await reabrirSolicitacao(request, alvo.sol.familia, alvo.sol.id, alvo.alvo, motivo);
    try {
      await moverEtapaSolicitacao(request, alvo.sol.familia, alvo.sol.id, alvo.coluna.id);
    } catch (e) {
      /* toast automático do wrapper; o reload reconcilia */
    }
    setReabrir(null);
    recarregarSilencioso();
  }, [reabrir, request, recarregarSilencioso]);

  const confirmarEncerrar = useCallback(
    async (acao, motivo, opcoes = {}) => {
      const sol = encerrar;
      if (!sol) return;
      if (acao === "recusar") {
        await recusarSolicitacao(request, sol.familia, sol.id, motivo);
      } else {
        await cancelarSolicitacao(request, sol.familia, sol.id, motivo, opcoes);
      }
      setEncerrar(null);
      recarregarSilencioso();
    },
    [encerrar, request, recarregarSilencioso],
  );

  return (
    <>
      {/* KPIs (clicáveis: filtram por situação), escopados nos tipos filtrados. */}
      <StatsRow>
        <StatChip
          type="button"
          $active={situacaoKpiAtiva("ABERTA")}
          onClick={() => toggleKpiSituacao("ABERTA")}
        >
          <strong>{kpis.abertas}</strong> aguardando
        </StatChip>
        <StatChip
          type="button"
          $active={situacaoKpiAtiva("AUTORIZADA")}
          onClick={() => toggleKpiSituacao("AUTORIZADA")}
        >
          <strong>{kpis.autorizadas}</strong> autorizadas
        </StatChip>
        <StatChip
          type="button"
          $active={situacaoKpiAtiva("EM_ANDAMENTO")}
          onClick={() => toggleKpiSituacao("EM_ANDAMENTO")}
        >
          <strong>{kpis.emAndamento}</strong> em andamento
        </StatChip>
        <StatChip
          type="button"
          $active={kpiAtendidasMesAtivo}
          onClick={toggleKpiAtendidasMes}
        >
          <strong>{kpis.atendidasMes}</strong> atendidas no mês
        </StatChip>
      </StatsRow>

      {/* Busca + filtros por dimensão (padrão Excel) + vista, numa linha. */}
      <ControlesBar>
        <div className="search">
          <FiSearch size={14} />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar número, pedido, cliente ou solicitante…"
          />
        </div>
        <FexBar>
          <FiltroExcel
            rotulo="Tipo"
            opcoes={opcoesTipo}
            selecionadas={fTipos}
            onChange={(v) => { marcarMexido(); setFTipos(v); }}
            comBusca={false}
          />
          <FiltroExcel
            rotulo="Situação"
            opcoes={opcoesSituacao}
            selecionadas={fSituacoes}
            onChange={(v) => { marcarMexido(); setFSituacoes(v); }}
            comBusca={false}
          />
          <FiltroExcel
            rotulo="Cliente"
            opcoes={opcoesCliente}
            selecionadas={fClientes}
            onChange={(v) => { marcarMexido(); setFClientes(v); }}
          />
          <FiltroExcel
            rotulo="Solicitante"
            opcoes={opcoesSolicitante}
            selecionadas={fSolicitantes}
            onChange={(v) => { marcarMexido(); setFSolicitantes(v); }}
          />
          <FiltroPeriodo
            valor={fPeriodo}
            onChange={(v) => { marcarMexido(); setFPeriodo(v); }}
          />
        </FexBar>
        <ViewToggle role="group" aria-label="Modo de visualização">
          {VISTAS.map(({ value, label, Icon }) => (
            <ToggleBtn
              key={value}
              type="button"
              $active={vista === value}
              onClick={() => { marcarMexido(); setVista(value); }}
              title={`Vista em ${label.toLowerCase()}`}
            >
              <Icon size={14} /> {label}
            </ToggleBtn>
          ))}
        </ViewToggle>
        {vista === "kanban" && podeMover && (
          <GhostBtn
            type="button"
            onClick={() => setConfigColunasAberto(true)}
            title="Configurar as colunas do kanban"
          >
            <FiSliders size={14} /> Colunas
          </GhostBtn>
        )}
        {/* Aparece assim que a vista ou os filtros mudam em relação ao
            padrão salvo; salvar vale em qualquer dispositivo. Só com a
            preferência carregada ('pronto'): salvar com o cache vazio
            apagaria as outras preferências do usuário no servidor. */}
        {visMudou && visHidratada && estadoPref === "pronto" && (
          <AsyncButton
            variant="secondary"
            style={{ height: 32, padding: "0 12px", fontSize: 12.5 }}
            title="Salvar esta vista e estes filtros como padrão desta página (vale em qualquer dispositivo)"
            successMessage="Visualização padrão salva — vale em qualquer dispositivo."
            errorMessage="Não foi possível salvar a visualização padrão."
            onClick={salvarVisualizacao}
          >
            <FiSave size={14} /> Salvar visualização padrão
          </AsyncButton>
        )}
      </ControlesBar>

      {/* Chips dos filtros ativos (remoção individual + limpar tudo). */}
      {temFiltros && (
        <FexChipsRow>
          {fTipos && (
            <FexChip>
              Tipo:&nbsp;<span className="vals">{resumoChip(fTipos)}</span>
              <button
                type="button"
                aria-label="Remover filtro de tipo"
                onClick={() => setFTipos(null)}
              >
                <FiX size={12} />
              </button>
            </FexChip>
          )}
          {fSituacoes && (
            <FexChip>
              Situação:&nbsp;
              <span className="vals">
                {resumoChip(fSituacoes, SITUACAO_LABEL_CURTO)}
              </span>
              <button
                type="button"
                aria-label="Remover filtro de situação"
                onClick={() => setFSituacoes(null)}
              >
                <FiX size={12} />
              </button>
            </FexChip>
          )}
          {fClientes && (
            <FexChip>
              Cliente:&nbsp;
              <span className="vals">
                {resumoChip(fClientes, null, "(Sem cliente)")}
              </span>
              <button
                type="button"
                aria-label="Remover filtro de cliente"
                onClick={() => setFClientes(null)}
              >
                <FiX size={12} />
              </button>
            </FexChip>
          )}
          {fSolicitantes && (
            <FexChip>
              Solicitante:&nbsp;
              <span className="vals">
                {resumoChip(fSolicitantes, null, "(Sem solicitante)")}
              </span>
              <button
                type="button"
                aria-label="Remover filtro de solicitante"
                onClick={() => setFSolicitantes(null)}
              >
                <FiX size={12} />
              </button>
            </FexChip>
          )}
          {(fPeriodo.de || fPeriodo.ate) && (
            <FexChip>
              Período:&nbsp;
              <span className="vals">
                {(fPeriodo.de || "…").split("-").reverse().join("/")} –{" "}
                {(fPeriodo.ate || "…").split("-").reverse().join("/")}
              </span>
              <button
                type="button"
                aria-label="Remover filtro de período"
                onClick={() => setFPeriodo({ de: null, ate: null })}
              >
                <FiX size={12} />
              </button>
            </FexChip>
          )}
          <GhostBtn
            type="button"
            style={{ height: 26, padding: "0 10px", fontSize: 12 }}
            onClick={limparFiltros}
          >
            Limpar filtros
          </GhostBtn>
          <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
            {filtradas.length} de {itens.length}
          </span>
        </FexChipsRow>
      )}

      {erro && <ErrorMsg>{erro}</ErrorMsg>}

      {loading ? (
        <Carregando />
      ) : filtradas.length === 0 && vista !== "linha" && vista !== "calendario" ? (
        <Vazio titulo="Nenhuma solicitação encontrada com os filtros atuais" />
      ) : vista === "kanban" ? (
        <Kanban
          itens={filtradas}
          onAbrir={abrirDetalhe}
          onMover={aoMoverKanban}
          podeMover={podeMover}
          colunas={colunasKanban}
          mostrarSelecao={mostrarSelecao}
          selecao={selecao}
          selecionadas={selecionadas}
          onToggleSelecao={toggleSelecao}
        />
      ) : vista === "linha" ? (
        <LinhaTempo
          request={request}
          itens={semFiltroDePeriodo}
          periodo={fPeriodo}
          versao={itens}
          onAbrir={abrirDetalhe}
        />
      ) : vista === "calendario" ? (
        <Calendario itens={filtradas} onAbrir={abrirDetalhe} />
      ) : (
        <Tabela
          itens={filtradas}
          colunas={colunasKanban}
          selecionadas={selecionadas}
          mostrarSelecao={mostrarSelecao}
          selecao={selecao}
          onToggleSelecao={toggleSelecao}
          onToggleTodas={toggleTodas}
          onAbrir={abrirDetalhe}
          onAutorizar={(s) => setAutorizar({ sol: s })}
        />
      )}

      {mostrarSelecao && solSelecionadas.length > 0 && (
        <StickyBar>
          <StickyInfo>
            {solSelecionadas.length}{" "}
            {solSelecionadas.length === 1 ? "solicitação selecionada" : "solicitações selecionadas"}
            {loteDaSelecao && (
              <>
                {" "}· {totalPecasSelecionadas}{" "}
                {totalPecasSelecionadas === 1 ? "peça MDF" : "peças MDF"}
              </>
            )}
            {pedidosSelecionados.length > 1 && (
              <> · {pedidosSelecionados.length} pedidos</>
            )}
          </StickyInfo>
          <StickyActions>
            <GhostBtn type="button" onClick={() => setSelecionadas(new Set())}>
              Limpar seleção
            </GhostBtn>
            {podeJuntar && (
              <GhostBtn
                type="button"
                disabled={Boolean(motivoSemJuncao)}
                title={motivoSemJuncao || "Juntar as selecionadas numa só"}
                onClick={() => setJuntarAberto(true)}
              >
                Juntar…
              </GhostBtn>
            )}
            {podeGerarLote && (
              <PrimaryBtn
                type="button"
                disabled={!loteDaSelecao}
                title={loteDaSelecao ? undefined : "Só assistências autorizadas com peças MDF sem lote entram em lote."}
                onClick={() => setLoteAberto(true)}
              >
                Gerar lote + baixar .xls
              </PrimaryBtn>
            )}
          </StickyActions>
        </StickyBar>
      )}

      {detalhe && (
        <DetalheSlideOver
          familia={detalhe.familia}
          id={detalhe.id}
          numero={detalhe.numero}
          onClose={() => setDetalhe(null)}
          onChanged={recarregarSilencioso}
          onAbrirOutra={setDetalhe}
        />
      )}

      {juntarAberto && !motivoSemJuncao && (
        <JuntarModal
          solicitacoes={solSelecionadas}
          onClose={() => setJuntarAberto(false)}
          onJuntada={aoJuntar}
        />
      )}

      {loteAberto && loteDaSelecao && (
        <GerarLoteModal
          pedidosSelecionados={pedidosSelecionados}
          clienteNome={solSelecionadas[0]?.cliente_nome || ""}
          solicitacoes={solSelecionadas}
          onClose={() => setLoteAberto(false)}
          onSuccess={aoGerarLote}
        />
      )}

      {reabrir && (
        <ReabrirModal
          solicitacao={reabrir.sol}
          situacaoAlvo={reabrir.alvo}
          onConfirmar={confirmarReabrir}
          onClose={() => setReabrir(null)}
        />
      )}

      {retirar && (
        <RetiradaModal
          familia={retirar.sol.familia}
          id={retirar.sol.id}
          numero={retirar.sol.numero}
          detalhe={retirar.sol}
          onClose={() => setRetirar(null)}
          onDone={aposRetirar}
        />
      )}

      {autorizar && (
        <AutorizarClassificarModal
          familia={autorizar.sol.familia}
          id={autorizar.sol.id}
          numero={autorizar.sol.numero}
          modo="autorizar"
          onClose={() => setAutorizar(null)}
          onDone={aposAutorizar}
        />
      )}

      {encerrar && (
        <EncerrarSolicitacaoModal
          solicitacao={encerrar}
          onConfirmar={confirmarEncerrar}
          onClose={() => setEncerrar(null)}
        />
      )}

      {configColunasAberto && (
        <ColunasKanbanPanel
          open
          tipo={tipoAba}
          onClose={() => setConfigColunasAberto(false)}
          onSaved={carregarColunas}
        />
      )}

      {comprovantesDe && (
        <ComprovantesLoteModal
          loteId={comprovantesDe.id}
          numeroLote={comprovantesDe.numero}
          onClose={() => setComprovantesDe(null)}
        />
      )}
    </>
  );
}
